<template>
    <div
        :id="id"
        :class="classString"
    >
        <h2 class="lni-u-heading--3">
            {{ text.contactInformation }}
        </h2>
        <lni-radios
            :id="`${id}_anonymous`"
            class="lni-u-mv2"
            :labelText="text.anonymousLabel"
            :options="[
                {
                    text: text.yes,
                    value: text.yes,
                },
                {
                    text: text.no,
                    value: text.no,
                },
            ]"
            :isColumn="false"
            :required="true"
            :changeAction="`${id}/syncStore`"
            @change="toggleInformation"
        />
        <lni-informational-message
            :id="`${id}_anonymous-message`"
            level="Information"
            large="true"
            class="lni-u-pv2 lni-u-ph2"
        >
            <lni-html-element
                :id="`${id}_anonymous-message--yes`"
                class="lni-u-mv1"
                :htmlString="text.anonymousMessage"
            />
        </lni-informational-message>
        <lni-collapse
            :id="`${id}_collapse`"
            class="lni-u-reading-width--max lni-u-mt3"
        >
            <span
                v-if="forSelf"
                class="lni-u-type--bold"
            >
                {{ text.minorDisclaimer }}
            </span>
            <div class="lni-u-full-width lni-u-flex lni-u-flex-wrap">
                <lni-input-text
                    :id="`${id}_first-name`"
                    class="lni-u-mb2 lni-u-mr1"
                    :labelText="text.firstName"
                    :changeAction="`${id}/syncStore`"
                />
                <lni-input-text
                    :id="`${id}_last-name`"
                    class="lni-u-mb2"
                    :labelText="text.lastName"
                    :changeAction="`${id}/syncStore`"
                />
            </div>
            <lni-input-phone
                :id="`${id}_phone`"
                :labelText="text.phone"
                :isFieldset="false"
                :changeAction="`${id}/syncStore`"
            />
            <lni-input-email
                :id="`${id}_email`"
                class="lni-u-mb2"
                :labelText="text.email"
                :changeAction="`${id}/syncStore`"
            />
            <lni-input-address
                :id="`${id}_address`"
                :labelText="text.address"
                :isFieldset="false"
                :filled="true"
                state="WA"
                :changeAction="`${id}/syncStore`"
            />
            <div class="lni-u-form-width--base">
                <lni-select
                    :id="`${id}_preferred-language`"
                    class="lni-u-mb2 lni-u-full-width"
                    :labelText="text.preferredLanguage"
                    :options="languageOptions"
                    :changeAction="`${id}/syncStore`"
                    @change="toggleField(`${id}_language-collapse`, `${id}_other-language`, hasOtherLanguage)"
                />
                <lni-collapse
                    :id="`${id}_language-collapse`"
                    class="lni-u-form-width--base"
                >
                    <lni-input-text
                        :id="`${id}_other-language`"
                        class="lni-u-mb2"
                        :labelText="text.otherLanguage"
                        maxlength="50"
                        :required="true"
                        :fullWidth="true"
                        :changeAction="`${id}/syncStore`"
                    />
                </lni-collapse>
            </div>
        </lni-collapse>
    </div>
</template>

<script>
import { mapGetters } from 'vuex';

export default {
    name : 'LniChildLaborComplaintContact',
    props: {
        classString: {
            type   : String,
            default: '',
        },
        text: {
            type    : Object,
            required: true,
        },
    },
    emits   : ['update-add'],
    computed: {
        hasOtherLanguage() {
            const value = this.$store.state[`${ this.id }_preferred-language`]?.value;
            const otherOption = this.$store.getters['appState/selectedOptionByText']('languageOptions', this.text.other);
            return value ? value === otherOption.value : false;
        },
        isAnonymous() {
            return this.$store.state[`${ this.id }_anonymous`].value === this.text.yes;
        },
        ...mapGetters({
            forSelf        : 'appState/forSelf',
            languageOptions: 'appState/languageOptions',
        }),
    },
    mounted() {
        this.$store.commit('setAttribute', {
            id       : this.id,
            attribute: 'trackedErrors',
            value    : [
                `${ this.id }_anonymous`,
                `${ this.id }_first-name`,
                `${ this.id }_last-name`,
                `${ this.id }_phone`,
                `${ this.id }_email`,
                `${ this.id }_address`,
                `${ this.id }_preferred-language`,
            ],
        }, { root: true });

        this.$emit('update-add', this.trackedErrors);
    },
    methods: {
        /**
         * Toggles the visibility of a field and its `<lni-collapse>` wrapper, and clears the value of the field if hidden.
         * @param collapseId The `id` of the `<lni-collapse>` wrapper
         * @param targetId The `id` of the field
         * @param visible Whether the field is visible or not
         */
        async toggleField(collapseId, targetId, visible) {
            this.$store.commit('setAttribute', {
                id       : collapseId,
                attribute: 'visible',
                value    : visible,
            }, { root: true });

            if (visible) {
                const targetState = this.$store.state[targetId];
                if (targetState.required) {
                    this.$store.commit(`${ this.id }/ADD_TRACKED_ERRORS`, [targetId]);
                    this.$emit('update-add', [targetId]);
                }
            } else {
                this.$store.commit('setAttribute', {
                    id       : targetId,
                    attribute: 'value',
                    value    : '',
                }, { root: true });

                this.$store.commit(`${ this.id }/REMOVE_TRACKED_ERRORS`, [targetId]);
                this.$emit('update-remove', [targetId]);
                await this.$store.dispatch(`${ this.id }/syncStore`);
            }
        },
        toggleInformation() {
            this.$store.commit('setAttribute', {
                id       : `${ this.id }_collapse`,
                attribute: 'visible',
                value    : !this.isAnonymous,
            }, { root: true });

            // Clear fields in collapse if anonymous is selected
            if (this.isAnonymous) {
                const ids = [
                    `${ this.id }_first-name`,
                    `${ this.id }_last-name`,
                    `${ this.id }_email`,
                    `${ this.id }_preferred-language`,
                    `${ this.id }_other-language`,
                ];
                ids.forEach(id => {
                    this.$store.commit('setAttribute', {
                        id,
                        attribute: 'value',
                        value    : '',
                    }, { root: true });

                    this.$store.commit('setAttribute', {
                        id,
                        attribute: 'value',
                        value    : '',
                    }, { root: true });
                });

                const addressId = `${ this.id }_address`;
                const addressAttributes = [
                    'address1',
                    'address2',
                    'city',
                    'state',
                    'zip',
                ];
                addressAttributes.forEach(attribute => {
                    const isState = attribute === 'state';

                    this.$store.commit('setAttribute', {
                        id   : addressId,
                        attribute,
                        value: isState ? 'WA' : '',
                    }, { root: true });

                    this.$store.commit('setAttribute', {
                        id       : `${ addressId }_${ attribute }`,
                        attribute: 'value',
                        value    : isState ? 'WA' : '',
                    }, { root: true });
                });

                const phoneId = `${ this.id }_phone`;
                const phoneAttributes = ['phone', 'ext'];
                phoneAttributes.forEach(attribute => {
                    this.$store.commit('setAttribute', {
                        id   : phoneId,
                        attribute,
                        value: '',
                    }, { root: true });

                    this.$store.commit('setAttribute', {
                        id       : `${ phoneId }_${ attribute }`,
                        attribute: 'value',
                        value    : '',
                    }, { root: true });
                });

                this.$store.dispatch(`${ this.id }/syncStore`);
            }
        },
    },
};
</script>